// The chat page's whole data layer — threads, transcript, sending. core.md §9.
//
// One hook, because the six behaviours of §9.5 are not separable: a send that
// fails has to put the question BACK in the composer, a send that succeeds has
// to adopt the thread the server created, and a send that returns after the
// student moved to another conversation must land in the thread it was asked
// in and say so. Split across three hooks those hand-offs become props.
//
// ── What it never does ────────────────────────────────────────────────────
//
//   * It never writes to the record. The chat is a reader of the record and a
//     writer of nothing but its own transcript (core.md §3.4).
//   * It never sends a student id — `askAboutCase` has no such argument and
//     never will (core.md §3.2).
//   * It never re-issues an AI mutation on a refetch. Every AI field is a
//     MUTATION because it spends the student's پارسه; nothing here retries one
//     automatically, and «تلاش مجدد» is always a key the student pressed.
//
// ── The two ids ───────────────────────────────────────────────────────────
//
// `threadId` (the URL) and `loadedThreadIdRef` (what the transcript in state
// belongs to) are deliberately separate. The first question of a fresh
// conversation creates a thread server-side; the screen then REPLACES the URL
// with `/chat/<id>`, which changes `threadId` — and without the ref the load
// effect would immediately re-fetch the transcript the answer just delivered,
// flashing the answer away and spending a round trip to get it back.
//
// ── A send is bound to the thread it was sent from ────────────────────────
//
// The in-flight turns live in a map keyed by the conversation they were asked
// in (`'new'` for the unsaved one), and the page only shows the entry of the
// thread it is looking at. So when the student walks to thread B while A is
// waiting for its answer, B renders its own transcript with an enabled
// composer and no typing block; the pending bubble reappears if they come back
// to A before the answer lands; and the answer itself is written into A by the
// BFF whatever the student is looking at (core.md §9.5). The toast «پاسخ در
// گفتگوی قبلی ثبت شد» fires ONLY when they are elsewhere at that moment.

import React from 'react';

import { bffQuery, bffMutation, errorCode, ERROR_CODES } from '../lib/bffClient.js';
import {
  CASE_CHAT_THREADS_QUERY,
  CASE_CHAT_THREAD_QUERY,
  ASK_ABOUT_CASE_MUTATION,
  RENAME_CHAT_THREAD_MUTATION,
  DELETE_CHAT_THREAD_MUTATION,
} from '../graphql/documents.js';
import { questionProblem, autoTitle } from '../lib/conversation.js';
import { readDraft, writeDraft, clearDraft, chatDraftKey } from '../lib/drafts.js';
import { startReveal } from '../lib/reveal.js';
import { refreshBalance } from './useMe.js';

/** The newest N messages a page holds; «پیام‌های قدیمی‌تر» asks for another N. */
export const MESSAGE_PAGE = 60;

/** Patient-level threads only — the copilot's encounter threads belong to the visit. */
export const PATIENT_SCOPE = 'PATIENT';

/**
 * The notice the composer area shows, if any. A closed set so a screen can
 * branch on it without parsing a message:
 *
 *   'credits'     INSUFFICIENT_CREDITS — inline card + «شارژ»; the question is
 *                 back in the composer, never a retry (core.md §3.5)
 *   'invalid'     INVALID_INPUT, or the client-side `questionProblem`
 *   'reserved' / 'tooLong' / 'empty'   the client-side validations
 *   'threadGone'  THREAD_NOT_FOUND — the conversation was deleted elsewhere;
 *                 the page is now a fresh one and the question is preserved
 */
export const NOTICES = ['credits', 'invalid', 'reserved', 'tooLong', 'empty', 'threadGone'];

let localSeq = 0;
const nextLocalId = () => `local-${(localSeq += 1)}`;

/** The map key of a conversation: its id, or `'new'` for the unsaved one. */
const keyOf = (threadId) => threadId || 'new';
const without = (map, key) => { const next = { ...map }; delete next[key]; return next; };

/**
 * @param caseId            the patient this conversation is about
 * @param threadId          the thread in the URL, or null for a fresh conversation
 * @param onThreadCreated   (id) => void — the first question created a thread;
 *                          the screen REPLACES the URL with it
 * @param onThreadGone      (reason) => void — the thread in the URL is gone; the
 *                          screen drops the id from the URL. `reason` is
 *                          'missing' (unknown, not mine, or deleted elsewhere —
 *                          the screen toasts «گفتگو پیدا نشد») or 'deleted'
 *                          (the student just deleted it here; no toast)
 * @param onMoved           () => void — an answer landed in a thread the student
 *                          has since left; the screen toasts «پاسخ در گفتگوی قبلی ثبت شد»
 */
export function useChat({
  caseId,
  threadId = null,
  onThreadCreated,
  onThreadGone,
  onMoved,
} = {}) {
  const [threads, setThreads] = React.useState([]);
  const [threadsLoading, setThreadsLoading] = React.useState(Boolean(caseId));
  const [threadsError, setThreadsError] = React.useState(null);
  const [threadsNonce, setThreadsNonce] = React.useState(0);

  const [thread, setThread] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [loading, setLoading] = React.useState(Boolean(threadId));
  const [error, setError] = React.useState(null);
  const [hasOlder, setHasOlder] = React.useState(false);
  const [loadingOlder, setLoadingOlder] = React.useState(false);

  // In-flight and failed student turns, keyed by the thread they belong to
  // (`'new'` for the unsaved conversation — see `keyOf`). See the header.
  const [inFlight, setInFlight] = React.useState({});
  const [failed, setFailed] = React.useState({});
  const [notice, setNotice] = React.useState(null);
  const [answer, setAnswer] = React.useState(null);         // confidence + follow-ups of the LAST answer
  const [reveal, setReveal] = React.useState(null);         // { id, text } while revealing

  // What the transcript in state belongs to (see the header). It starts NULL
  // even when the URL names a thread: seeding it with `threadId` would make the
  // load effect's "we already have this one" check true on the first run, and
  // the page would open a thread it never read.
  const loadedRef = React.useRef(null);
  // Where the student IS, read inside an async send to decide whether the answer
  // landed in the conversation they are still looking at.
  const currentRef = React.useRef(threadId || null);
  const revealRef = React.useRef(null);

  // ── the composer draft, per thread (core.md §15) ────────────────────────
  const draftKey = chatDraftKey({ threadId, caseId });
  const [draft, setDraftState] = React.useState(() => readDraft(draftKey) || '');

  React.useEffect(() => {
    setDraftState(readDraft(draftKey) || '');
  }, [draftKey]);

  const setDraft = React.useCallback((value) => {
    setDraftState(value);
    writeDraft(draftKey, value);
    // «گفتگو پیدا نشد» has said its piece once the student starts typing again.
    setNotice((n) => (n === 'threadGone' ? null : n));
  }, [draftKey]);

  // ── the sidebar's list ─────────────────────────────────────────────────
  const reloadThreads = React.useCallback(() => setThreadsNonce((n) => n + 1), []);

  React.useEffect(() => {
    if (!caseId) { setThreads([]); setThreadsLoading(false); return undefined; }
    let alive = true;
    setThreadsLoading(true);
    bffQuery(CASE_CHAT_THREADS_QUERY, { caseId, scope: PATIENT_SCOPE, encounterId: null })
      .then((data) => {
        if (!alive) return;
        setThreads(data?.caseChatThreads || []);
        setThreadsError(null);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[useChat] threads failed:', errorCode(err) || err?.message);
        setThreadsError(err);
        setThreads([]);
      })
      .finally(() => { if (alive) setThreadsLoading(false); });
    return () => { alive = false; };
  }, [caseId, threadsNonce]);

  // ── the transcript ─────────────────────────────────────────────────────
  React.useEffect(() => {
    currentRef.current = threadId || null;

    // A fresh conversation: nothing to read, and nothing written yet.
    if (!threadId) {
      if (loadedRef.current == null) { setLoading(false); return undefined; }
      loadedRef.current = null;
      setThread(null); setMessages([]); setHasOlder(false);
      setAnswer(null); setError(null); setLoading(false);
      return undefined;
    }

    // The answer we just received IS this thread — do not re-read it.
    if (loadedRef.current === threadId) { setLoading(false); return undefined; }

    // A different conversation is being opened: whatever was revealing belongs
    // to the one being left, and its message is already stored there.
    revealRef.current?.stop();
    revealRef.current = null;

    let alive = true;
    setLoading(true);
    setNotice(null);
    bffQuery(CASE_CHAT_THREAD_QUERY, { threadId, messageLimit: MESSAGE_PAGE, beforeSeq: null })
      .then((data) => {
        if (!alive) return;
        const row = data?.caseChatThread || null;
        if (!row) {
          // Deleted in another tab, or never this student's. Same copy for both
          // — a distinguishable "not yours" would be an oracle (core.md §3.2).
          loadedRef.current = null;
          setThread(null); setMessages([]); setHasOlder(false);
          setNotice('threadGone');
          onThreadGone?.('missing');
          return;
        }
        loadedRef.current = row.id;
        setThread(row);
        setMessages(row.messages || []);
        setHasOlder(Boolean(row.hasOlder));
        setAnswer(null);
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[useChat] thread failed:', errorCode(err) || err?.message);
        setError(err);
        setThread(null); setMessages([]);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // `onThreadGone` is intentionally not a dependency: it is a callback the
    // screen re-creates every render, and depending on it re-arms the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // Stop any reveal in flight when the page UNMOUNTS — a timer writing into a
  // dead component is a leak and a React warning.
  //
  // Unmount only, deliberately: this used to key on `threadId`, and the very
  // first answer of a fresh conversation changes `threadId` (the screen adopts
  // the id the answer created), so the cleanup killed the reveal of the answer
  // that had just arrived. A reveal that ends when the student MOVES to another
  // thread is handled where that actually happens — the load effect below.
  React.useEffect(() => () => { revealRef.current?.stop(); revealRef.current = null; }, []);

  /** «پیام‌های قدیمی‌تر» — a BUTTON, never a scroll trigger (core.md §9.3). */
  const loadOlder = React.useCallback(async () => {
    const first = messages[0];
    if (!threadId || !first || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const data = await bffQuery(CASE_CHAT_THREAD_QUERY, {
        threadId, messageLimit: MESSAGE_PAGE, beforeSeq: first.seq,
      });
      const row = data?.caseChatThread;
      setMessages((prev) => [...(row?.messages || []), ...prev]);
      setHasOlder(Boolean(row?.hasOlder));
    } catch (err) {
      console.error('[useChat] older failed:', errorCode(err) || err?.message);
    } finally {
      setLoadingOlder(false);
    }
  }, [threadId, messages, loadingOlder]);

  /** Reveal an answer in word chunks — the ChatGPT feel without a streaming RPC. */
  const beginReveal = React.useCallback((message) => {
    revealRef.current?.stop();
    if (!message?.content) { setReveal(null); return; }
    setReveal({ id: message.id, text: '' });
    revealRef.current = startReveal(message.content, {
      onUpdate: (partial, { done }) => {
        if (done) setReveal(null);
        else setReveal({ id: message.id, text: partial });
      },
    });
  }, []);

  /** «توقف» — jump to the end. The full message is already stored. */
  const stopReveal = React.useCallback(() => {
    revealRef.current?.stop();
    revealRef.current = null;
  }, []);

  // ── the send, and every row of §9.5 ────────────────────────────────────
  const send = React.useCallback(async (rawQuestion) => {
    const question = String(rawQuestion ?? '');
    const problem = questionProblem(question);
    if (problem) { setNotice(problem === 'empty' ? 'empty' : problem); return false; }

    setNotice(null);
    stopReveal();

    const askedIn = loadedRef.current;   // may be null: a fresh conversation
    const key = keyOf(askedIn);
    const localId = nextLocalId();
    setFailed((map) => without(map, key));
    setInFlight((map) => ({
      ...map,
      [key]: {
        id: localId, role: 'student', kind: 'chat', content: question,
        costParseh: 0, createdAtMs: String(Date.now()), status: 'pending',
      },
    }));
    setDraft('');

    let data;
    try {
      data = await bffMutation(ASK_ABOUT_CASE_MUTATION, {
        caseId, threadId: askedIn || null, question,
      });
    } catch (err) {
      const code = errorCode(err);
      setInFlight((map) => without(map, key));
      // Every recoverable failure puts the question BACK where the student can
      // press send again — nothing was stored and nothing was billed (§8.3).
      if (code === ERROR_CODES.INSUFFICIENT_CREDITS) { setDraft(question); setNotice('credits'); return false; }
      if (code === ERROR_CODES.INVALID_INPUT) { setDraft(question); setNotice('invalid'); return false; }
      if (code === ERROR_CODES.THREAD_NOT_FOUND) {
        // Deleted in another tab. Become a fresh conversation, keep the typing.
        loadedRef.current = null;
        setThread(null); setMessages([]); setHasOlder(false); setAnswer(null);
        // The draft is written under the FRESH conversation's key, not this
        // thread's: the screen is about to drop the id from the URL, and a
        // question saved under the dead thread's key would be read back as
        // empty a tick later — the typing lost by the recovery meant to keep it.
        writeDraft(chatDraftKey({ threadId: null, caseId }), question);
        setDraftState(question);
        setNotice('threadGone');
        onThreadGone?.('missing');
        reloadThreads();
        return false;
      }
      // A transport failure: the student's turn stays on screen as FAILED with
      // a retry, because that is the one case where re-sending can succeed.
      console.error('[useChat] ask failed:', code || err?.message);
      setFailed((map) => ({
        ...map,
        [key]: {
          id: localId, role: 'student', kind: 'chat', content: question,
          costParseh: 0, createdAtMs: String(Date.now()), status: 'failed',
        },
      }));
      return false;
    }

    const result = data?.askAboutCase || null;
    const answered = result?.thread || null;
    setInFlight((map) => without(map, key));
    // The wallet moved in another service; the balance line is a read of it.
    refreshBalance();
    reloadThreads();

    if (!answered) { setNotice('invalid'); return false; }

    // The student walked away mid-flight. The answer is stored in the thread it
    // was asked in — this page just says so and leaves it there (core.md §9.5).
    if (currentRef.current !== askedIn) {
      onMoved?.(answered.id);
      return true;
    }
    // Back on the right conversation, but its transcript is still being
    // re-read (they left and returned mid-flight): that read will deliver the
    // answer — the BFF stored it — and appending here would land it on a
    // transcript that is about to be replaced.
    if (askedIn && loadedRef.current !== askedIn) return true;

    const created = !askedIn;
    loadedRef.current = answered.id;
    currentRef.current = answered.id;
    setThread((prev) => ({ ...(prev || {}), ...answered, messages: undefined }));
    const fresh = answered.messages || [];
    setMessages((prev) => [...prev, ...fresh]);
    setAnswer({
      confidence: result.confidence || null,
      followUps: result.followUpSuggestions || [],
      references: result.relevantReferences || [],
    });
    beginReveal(fresh.find((m) => m.role === 'assistant'));
    if (created) onThreadCreated?.(answered.id);
    return true;
  }, [caseId, setDraft, beginReveal, stopReveal, reloadThreads, onThreadCreated, onThreadGone, onMoved]);

  // What THIS conversation is waiting on or failed at, if anything. The map
  // may hold entries for conversations the student has walked away from; those
  // stay where they are and render again only when the student returns.
  const currentKey = keyOf(threadId);
  const pending = inFlight[currentKey] || null;
  const failedTurn = failed[currentKey] || null;

  /** Re-send the turn that failed. The failed bubble is dropped first. */
  const retry = React.useCallback(() => {
    const text = failedTurn?.content;
    if (!text) return Promise.resolve(false);
    setFailed((map) => without(map, currentKey));
    return send(text);
  }, [failedTurn, currentKey, send]);

  /**
   * «بازتولید» — ask the SAME question again. The old answer stays in history:
   * the store appends, it never replaces (core.md §9.3).
   */
  const regenerate = React.useCallback((assistantMessage) => {
    const idx = messages.findIndex((m) => m.id === assistantMessage?.id);
    for (let i = idx - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'student') return send(messages[i].content);
    }
    return Promise.resolve(false);
  }, [messages, send]);

  /** Rename a thread. An EMPTY title restores the automatic one (core.md §9.5). */
  const rename = React.useCallback(async (id, title) => {
    const data = await bffMutation(RENAME_CHAT_THREAD_MUTATION, { threadId: id, title: title || '' });
    const row = data?.renameCaseChatThread || null;
    if (row) {
      setThreads((prev) => prev.map((t) => (t.id === row.id ? { ...t, ...row } : t)));
      setThread((prev) => (prev && prev.id === row.id ? { ...prev, ...row } : prev));
    }
    return row;
  }, []);

  const remove = React.useCallback(async (id) => {
    await bffMutation(DELETE_CHAT_THREAD_MUTATION, { threadId: id });
    setThreads((prev) => prev.filter((t) => t.id !== id));
    clearDraft(chatDraftKey({ threadId: id, caseId }));
    if (id === loadedRef.current) {
      loadedRef.current = null;
      setThread(null); setMessages([]); setHasOlder(false); setAnswer(null);
      onThreadGone?.('deleted');
    }
    reloadThreads();
  }, [caseId, onThreadGone, reloadThreads]);

  /** «گفتگوی جدید» — an unsaved conversation; no row exists until the first send. */
  const startNew = React.useCallback(() => {
    stopReveal();
    loadedRef.current = null;
    currentRef.current = null;
    setThread(null); setMessages([]); setHasOlder(false);
    setAnswer(null); setNotice(null);
    setFailed((map) => without(map, 'new'));
  }, [stopReveal]);

  const dismissNotice = React.useCallback(() => setNotice(null), []);

  // What the column renders: the stored transcript, then THIS conversation's
  // turn in flight or the one that failed. Only one of those two can exist at a
  // time for one conversation.
  const visibleMessages = React.useMemo(() => {
    const out = [...messages];
    if (pending) out.push(pending);
    if (failedTurn) out.push(failedTurn);
    return out;
  }, [messages, pending, failedTurn]);

  // The title an unsaved conversation will get once its first answer lands —
  // the same clip the BFF stores, so nothing flashes when it does (§9.5).
  const pendingTitle = !threadId && pending ? autoTitle(pending.content) : null;

  return {
    // sidebar
    threads, threadsLoading, threadsError, reloadThreads,
    // transcript
    thread, messages: visibleMessages, storedMessages: messages,
    loading, error, hasOlder, loadingOlder, loadOlder,
    // send lifecycle
    sending: Boolean(pending), pendingTitle,
    revealing: Boolean(reveal), reveal, answer, notice, dismissNotice,
    send, retry, regenerate, stopReveal, startNew,
    // thread admin
    rename, remove,
    // composer draft
    draft, setDraft,
  };
}

export default useChat;
